import {useEffect, useRef, useState} from 'react';

import {isUnlimitedSubscription} from 'src/modules/profile/account/tabs/account-profile/product-access-summary-section/subscription-seat-text';
import {updateUserDetails} from 'src/openapi/admin/apis/UpdateUserDetailsApi';
import {assignSeatToUser, removeSeatFromUser} from 'src/openapi/admin/apis/UserSeatAssignmentsApi';
import {SeatAssignRequestState} from 'src/openapi/admin/models/SeatAssignRequest';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import {changedSeats, draftFromSnapshot, loadAccessSnapshot, platformChanged, readAccessDetails} from './manage-seats-data';

import type {AccessDraft, AccessPermissions, AccessSnapshot} from './manage-seats-data';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';

type Props = {
    userId: number, accountId: number, permissions: AccessPermissions, onChanged: (details: UserDetails) => void,
};
const message = (error: unknown) => error instanceof Error ? error.message :
    getError({error: error as import('src/openapi/admin/models/Error').Error, separator: '. '}) ||
    'Unable to save changes. Please retry.';

const reconcile = (fresh: AccessSnapshot, previous: AccessSnapshot | null, draft: AccessDraft | null) => {
    const next = draftFromSnapshot(fresh);
    if (previous && draft) {
        if (platformChanged(previous, draft)) {
            next.typeCode = draft.typeCode;
            next.statusCode = draft.statusCode;
        }
        changedSeats(previous, draft).forEach(([id, granted]) => {
            if (id in next.seats || fresh.assigned.has(id) !== granted) {
                next.seats[id] = granted;
            }
        });
    }
    return next;
};

const unavailableTermError = (snapshot: AccessSnapshot, draft: AccessDraft): Record<string, string> => {
    const ids = new Set(snapshot.terms.map(term => term.productSubscriptionId));
    return changedSeats(snapshot, draft).some(([id]) => !ids.has(id)) ? {
        general: 'Some edited Product Access Terms are no longer available. Their changes were not saved. ' +
            'Close and reopen to review current access.',
    } : {};
};

export const useManageSeats = ({userId, accountId, permissions, onChanged}: Props) => {
    const dispatch = useAppDispatch();
    const [snapshot, setSnapshot] = useState<AccessSnapshot | null>(null);
    const [draft, setDraft] = useState<AccessDraft | null>(null);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [version, setVersion] = useState(0);
    const pending = useRef(false);
    const needsRefresh = useRef(false);
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;
    const stateRef = useRef({snapshot, draft});
    stateRef.current = {snapshot, draft};
    const {platform, products, grant, revoke} = permissions;
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setLoadError('');
        loadAccessSnapshot(dispatch,
            {userId, accountId, permissions: {platform, products, grant, revoke}}, controller.signal)
            .then(fresh => {
                if (!controller.signal.aborted) {
                    const next = reconcile(fresh, stateRef.current.snapshot, stateRef.current.draft);
                    setDraft(next);
                    setSnapshot(fresh);
                    if (needsRefresh.current) {
                        onChangedRef.current(fresh.details);
                        needsRefresh.current = false;
                        setErrors(unavailableTermError(fresh, next));
                    }
                }
            }).catch(error => {
                if (!controller.signal.aborted) {
                    setLoadError(message(error));
                }
            }).finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => controller.abort();
    }, [dispatch, userId, accountId, platform, products, grant, revoke, version]);

    const canGrant = (id: string) => {
        const term = snapshot?.terms.find(item => item.productSubscriptionId === id);
        return !!(products && term && (snapshot?.assigned.has(id) ||
            (grant && (isUnlimitedSubscription(term) || term.activeSeatCount < term.seats))));
    };
    const canRevoke = (id: string) => products && (!snapshot?.assigned.has(id) || revoke);
    const changeSeat = (id: string, granted: boolean) => {
        if (busy || !products || !snapshot?.terms.some(term => term.productSubscriptionId === id) ||
            (granted ? !canGrant(id) : !canRevoke(id))) {
            return;
        }
        setDraft(value => value ? {...value, seats: {...value.seats, [id]: granted}} : value);
    };
    const changeType = (typeCode: string) => {
        if (!platform || busy || (typeCode === 'T_NA' && !snapshot?.statuses.includes('EMPTY'))) {
            return;
        }
        setDraft(value => {
            if (!value) {
                return value;
            }
            let statusCode = value.statusCode;
            if (typeCode === 'T_NA') {
                statusCode = 'EMPTY';
            } else if (statusCode === 'EMPTY') {
                statusCode = snapshot?.statuses.find(status => status !== 'EMPTY') || statusCode;
            }
            return {...value, typeCode, statusCode};
        });
    };
    const changeStatus = (statusCode: string) => {
        if (!platform || busy || !snapshot?.statuses.includes(statusCode)) {
            return;
        }
        setDraft(value => {
            if (!value) {
                return value;
            }
            let typeCode = value.typeCode;
            if (statusCode === 'EMPTY') {
                typeCode = 'T_NA';
            } else if (typeCode === 'T_NA') {
                typeCode = 'T_LC';
            }
            return {...value, typeCode, statusCode};
        });
    };
    const dirty = !!(snapshot && draft && (platformChanged(snapshot, draft) || changedSeats(snapshot, draft).length));
    const save = async () => {
        if ((!platform && !grant && !revoke) || pending.current || !snapshot || !draft || !dirty ||
            loading || loadError || reason.length > 64) {
            return false;
        }
        pending.current = true;
        needsRefresh.current = true;
        setBusy(true);
        const failures: Record<string, string> = {};
        setErrors({});
        const auditReason = reason.trim() || undefined;
        try {
            if (platform && platformChanged(snapshot, draft)) {
                try {
                    const current = await readAccessDetails(dispatch, userId);
                    const response = await dispatch(updateUserDetails({userId, userDetailsSaveRequest: {
                        ...current, typeCode: draft.typeCode, statusCode: draft.statusCode, reason: auditReason,
                    }}));
                    if (!response.payload?.success) {
                        throw response.payload?.data;
                    }
                } catch (error) {
                    failures.platform = message(error);
                }
            }
            for (const [id, granted] of changedSeats(snapshot, draft).sort((a, b) => Number(a[1]) - Number(b[1]))) {
                try {
                    if (!products || (granted ? !canGrant(id) : !revoke)) {
                        throw new Error('This access change is no longer available.');
                    }
                    const request = {userId, seatAssignRequest: {productSubscriptionId: id, reason: auditReason,
                        state: granted ? SeatAssignRequestState.ASSIGN : SeatAssignRequestState.REMOVE}};
                    const response = granted ? await dispatch(assignSeatToUser(request)) :
                            await dispatch(removeSeatFromUser(request));
                    if (!response.payload?.success) {
                        throw response.payload?.data;
                    }
                } catch (error) {
                    failures[id] = message(error);
                }
            }
            const fresh = await loadAccessSnapshot(dispatch, {userId, accountId, permissions});
            const next = reconcile(fresh, snapshot, draft);
            const remaining = changedSeats(fresh, next);
            const platformPending = platformChanged(fresh, next);
            const remainingErrors: Record<string, string> = unavailableTermError(fresh, next);
            if (platformPending) {
                remainingErrors.platform = failures.platform || 'Platform changes were not saved. Please retry.';
            }
            remaining.forEach(([id]) => {
                remainingErrors[id] = failures[id] || 'Seat changes were not saved. Please retry.';
            });
            setSnapshot(fresh);
            setDraft(next);
            setErrors(remainingErrors);
            onChanged(fresh.details);
            needsRefresh.current = false;
            return !platformPending && !remaining.length;
        } catch (error) {
            setErrors(failures);
            setLoadError(`Unable to verify the saved state. Retry loading before applying again. ${message(error)}`);
            return false;
        } finally {
            pending.current = false;
            setBusy(false);
        }
    };
    return {draft, terms: snapshot?.terms || [], statuses: snapshot?.statuses || [], reason, setReason,
        loading, loadError, errors, busy, dirty, canGrant, canRevoke, changeSeat, changeType, changeStatus, save,
        retry: () => setVersion(value => value + 1)};
};
